import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { menuData } from '../data/menuData';
import { MenuItem } from '../types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>(['home']);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems.includes(item.id);
        const active = isActive(item.path);

        const buttonClasses = `w-full text-left flex items-center justify-between px-3 py-2 rounded-md ${
            active ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'
        } ${level > 0 ? 'pl-' + (2 * level) : ''} transition-colors duration-200`;

        return (
            <div key={item.id} className="w-full">
                <button
                    onClick={() => {
                        if (hasChildren) {
                            toggleExpanded(item.id);
                        } else {
                            navigate(item.path);
                            onClose();
                        }
                    }}
                    className={buttonClasses}
                >
                    <span className="flex-1">{item.title}</span>
                    {hasChildren && (
                        isExpanded ? <ChevronDown className="h-4 w-4 ml-2" /> : <ChevronRight className="h-4 w-4 ml-2" />
                    )}
                </button>

                {hasChildren && isExpanded && (
                    <div className="mt-1 space-y-1 text-[14px] ms-2">
                        {item.children!.map(child => renderMenuItem(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside className={`
        fixed overflow-auto left-0 top-16 xl:max-h-[calc(100vh-5.4rem)] rounded-r-lg w-64 bg-white z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto max-xl:top-0 max-xl:h-full
      `} >
                <div className="p-4">
                    <nav className="space-y-2">
                        {menuData.map(item => renderMenuItem(item))}
                    </nav>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
